import React, { useState, useEffect, useRef } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Mic, CheckCircle, Clock, AlertCircle, Video, AlertTriangle, Loader2, Globe } from 'lucide-react'
import { interviewAudioApi } from 'lib/api'
import { Modal } from 'components/ui/Modal'
import { Checkbox } from 'components/ui/Checkbox'
import { useTheme } from 'contexts/ThemeContext'
import { translateFrToEn } from 'lib/translationService'
import { Progress } from 'components/ui/Progress'
import { DeviceTestModal } from '../components/DeviceTestModal'
import { InterviewOnboardingModal } from '../components/InterviewOnboardingModal'

const InterviewCameraPreview = React.memo(function InterviewCameraPreview({
    videoRef,
}: {
    videoRef: React.RefObject<HTMLVideoElement | null>
}) {
    return (
        <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover transform scale-x-[-1]"
        />
    )
})

type InterviewState = 'device_test' | 'language_selection' | 'welcome' | 'recording' | 'uploading' | 'processing' | 'completed' | 'error' | 'already_completed' | 'deadline_exceeded'

export function InterviewPage() {
    const { t } = useTranslation()
    const { theme } = useTheme()
    const { slug } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const [candidateName, setCandidateName] = useState(location.state?.candidateName || t('interview.page.candidateDefault'))
    const [candidatePhoto, setCandidatePhoto] = useState(location.state?.candidatePhoto)
    const [offerTitle, setOfferTitle] = useState(location.state?.offerTitle || t('interview.page.sessionDefault'))
    const [currentQuestionData, setCurrentQuestionData] = useState<any>(location.state?.current_question || null)
    const [totalQuestionsCount, setTotalQuestionsCount] = useState(location.state?.total_questions_count || 0)
    const [interviewId, setInterviewId] = useState(location.state?.interview_id)
    const [language, setLanguage] = useState(location.state?.language)
    const [savedQuestionIndex, setSavedQuestionIndex] = useState(location.state?.current_question_index || 0)
    const initialConsentGiven = location.state?.consentGiven || false
    const initialConsentTimestamp = location.state?.consentTimestamp || null

    // State
    const [interviewState, setInterviewState] = useState<InterviewState>(() => {
        if (location.state?.is_deadline_passed) return 'deadline_exceeded'
        if (location.state?.status === 'completed' || location.state?.status === 'in_processing') {
            return 'already_completed'
        }
        return 'welcome'
    })
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(savedQuestionIndex)
    const [timeRemaining, setTimeRemaining] = useState(120)
    const [isRecording, setIsRecording] = useState(false)
    const [error, setError] = useState('')
    const [showDeviceTestModal, setShowDeviceTestModal] = useState(false)
    const [showOnboardingModal, setShowOnboardingModal] = useState(false)
    const [showConsentModal, setShowConsentModal] = useState(!initialConsentGiven)
    const [hasAcceptedConsent, setHasAcceptedConsent] = useState(initialConsentGiven)
    const [consentTimestamp, setConsentTimestamp] = useState<string | null>(initialConsentTimestamp)
    const [translatedQuestions, setTranslatedQuestions] = useState<Record<number, string>>({})
    const [isTranslating, setIsTranslating] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [isLoading, setIsLoading] = useState(() => {
        try {
            return Boolean(
                location.state?.interview_id ||
                localStorage.getItem('interview_id')
            )
        } catch {
            return false
        }
    })
    const [isUploading, setIsUploading] = useState(false)
    const [showSuccessToast, setShowSuccessToast] = useState(false)
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null)
    const logoSrc = theme === 'dark' ? '/logodark512.png' : '/logo512.png'

    // Sync state from backend on mount or if missing
    useEffect(() => {
        const fetchDetails = async () => {
            const storedId = localStorage.getItem('interview_id')
            const storedCode = localStorage.getItem('interview_access_code')

            const idToUse = interviewId || (storedId ? parseInt(storedId) : null)

            if (!idToUse || !storedCode) {
                // If we don't even have stored credentials and no state, we must log in
                if (!interviewId) {
                    navigate('/interview_audio/login')
                }
                setIsLoading(false)
                return
            }

            setIsLoading(true)
            try {
                const response = await interviewAudioApi.getInterviewDetails(idToUse, storedCode)
                const data = response.data

                // Update all states from backend truth
                setInterviewId(data.interview_id)
                setCandidateName(data.candidate_name)
                setCandidatePhoto(data.candidate_photo)
                setOfferTitle(data.offer_title)
                setCurrentQuestionData(data.current_question)
                setTotalQuestionsCount(data.total_questions_count)
                setLanguage(data.language)
                setSavedQuestionIndex(data.current_question_index)
                setCurrentQuestionIndex(data.current_question_index)

                if (data.consent_given) {
                    setHasAcceptedConsent(true)
                    setShowConsentModal(false)
                    setConsentTimestamp(data.consent_timestamp)
                }

                if (data.is_deadline_passed) {
                    setInterviewState('deadline_exceeded')
                } else if (data.status === 'completed' || data.status === 'in_processing') {
                    setInterviewState('already_completed')
                }

                if (data.language === 'custom') {
                    setShowConsentModal(true)
                } else if (data.language) {
                    i18n.changeLanguage(data.language)
                }
            } catch (err: any) {
                console.error('Failed to sync interview details:', err)
                if (!interviewId) {
                    setError(t('interview.page.sessionError', 'Failed to recover session. Please log in again.'))
                    setInterviewState('error')
                }
            } finally {
                setIsLoading(false)
            }
        }

        fetchDetails()
    }, [])

    // Check if interview is already completed or in processing
    useEffect(() => {
        const { status, is_deadline_passed } = location.state || {}

        if (is_deadline_passed) {
            setInterviewState('deadline_exceeded')
            setShowDeviceTestModal(false)
            setShowConsentModal(false)
            return
        }

        if (status === 'completed' || status === 'in_processing') {
            setInterviewState('already_completed')
            setShowDeviceTestModal(false)
            setShowConsentModal(false)
        }
    }, [location.state])

    // Demo only for candidates with no answers yet, before deadline / completion
    useEffect(() => {
        const statusFromRoute = location.state?.status
        const isDeadlinePassed =
            interviewState === 'deadline_exceeded' ||
            Boolean(location.state?.is_deadline_passed)
        const isAlreadyCompleted =
            interviewState === 'already_completed' ||
            statusFromRoute === 'completed' ||
            statusFromRoute === 'in_processing'
        const hasAnsweredAnyQuestion = savedQuestionIndex > 0

        const canShowDemo =
            !isLoading &&
            interviewState === 'welcome' &&
            !showConsentModal &&
            !isDeadlinePassed &&
            !isAlreadyCompleted &&
            !hasAnsweredAnyQuestion

        if (!canShowDemo) {
            setShowOnboardingModal(false)
            return
        }

        const storageKey = `interview_demo_seen_${interviewId || slug || 'session'}`
        if (sessionStorage.getItem(storageKey) === '1') {
            setShowOnboardingModal(false)
            return
        }

        setShowOnboardingModal(true)
    }, [
        isLoading,
        interviewState,
        showConsentModal,
        interviewId,
        slug,
        savedQuestionIndex,
        location.state,
    ])

    const completeOnboarding = () => {
        const storageKey = `interview_demo_seen_${interviewId || slug || 'session'}`
        sessionStorage.setItem(storageKey, '1')
        setShowOnboardingModal(false)
    }

    // Refs
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const chunksRef = useRef<Blob[]>([])
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const timerIntervalRef = useRef<number | null>(null)
    const recordingStartTimeRef = useRef<number>(0)
    const onDataAvailablePromiseRef = useRef<{ resolve: (blob: Blob) => void, reject: (err: any) => void } | null>(null)

    const { i18n } = useTranslation()
    const currentLang = i18n.language.split('-')[0]

    // Effect to translate questions when language is English
    useEffect(() => {
        const translateQuestion = async () => {
            // Only translate if the interview language is French (or unspecified) 
            // but the user's UI is set to English.
            // If the interview language is already English ('en'), questions are already in English.
            if (currentLang === 'en' && language !== 'en' && currentQuestionData?.text && !translatedQuestions[currentQuestionIndex]) {
                setIsTranslating(true)
                try {
                    const text = await translateFrToEn(currentQuestionData.text)
                    setTranslatedQuestions(prev => ({ ...prev, [currentQuestionIndex]: text }))
                } catch (err) {
                    console.error('Translation failed:', err)
                } finally {
                    setIsTranslating(false)
                }
            }
        }
        translateQuestion()
    }, [currentLang, language, currentQuestionData, currentQuestionIndex, translatedQuestions])

    const currentQuestion = (currentLang === 'en' && translatedQuestions[currentQuestionIndex])
        ? translatedQuestions[currentQuestionIndex]
        : currentQuestionData?.text || ''

    const currentQuestionDuration = currentQuestionData?.duration || 120
    const totalQuestions = totalQuestionsCount

    // Handle language selection
    const handleLanguageSelection = async (selectedLang: string) => {
        setIsLoading(true)
        try {
            const storedCode = localStorage.getItem('interview_access_code')
            if (!storedCode) throw new Error('No access code found')

            const response = await interviewAudioApi.startWithLanguage(storedCode, selectedLang)
            const data = response.data

            // Update state with new details
            setCurrentQuestionData(data.current_question)
            setTotalQuestionsCount(data.total_questions_count)
            setLanguage(data.language)
            setInterviewState('welcome')
            i18n.changeLanguage(data.language)

        } catch (err: any) {
            console.error('Failed to start interview with language:', err)
            setError(t('interview.page.languageSelectionError', 'Failed to set language'))
            setInterviewState('error')
        } finally {
            setIsLoading(false)
        }
    }

    // Start interview
    const startInterview = () => {
        // Show the device test modal instead of directly accessing devices
        setShowDeviceTestModal(true)
    }

    // Start recording after device test is successful
    const startRecordingAfterDeviceTest = async () => {
        try {
            const constraints = {
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1,
                    sampleRate: 48000
                    // sampleRate: 16000
                },
                video: {
                    width: { ideal: 140 },
                    height: { ideal: 140 },
                    frameRate: { ideal: 15 }
                }
            }

            const stream = await navigator.mediaDevices.getUserMedia(constraints)
            streamRef.current = stream
            setInterviewState('recording')
            // Pass the first question's duration explicitly
            startRecordingSegment(stream, currentQuestionData?.duration ?? 120)
        } catch (err) {
            setError(t('interview.page.accessDenied'))
            setInterviewState('error')
        }
    }

    const startRecordingSegment = (stream: MediaStream, questionDuration?: number) => {
        chunksRef.current = []

        // Determine supported MIME type (prefer VP9 then VP8)
        const types = [
            'video/webm;codecs=vp9,opus',
            'video/webm;codecs=vp8,opus',
            'video/webm',
            'video/mp4'
        ]

        let selectedType = ''
        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                selectedType = type
                break
            }
        }

        const options = {
            mimeType: selectedType,
            videoBitsPerSecond: 1000000, // 1 Mbps for better quality segments
            audioBitsPerSecond: 128000   // 128 kbps
        }

        console.log('Starting MediaRecorder for segment with options:', options)
        const mediaRecorder = new MediaRecorder(stream, options)
        mediaRecorderRef.current = mediaRecorder

        mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                chunksRef.current.push(event.data)
            }
        }

        mediaRecorder.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: selectedType })
            if (onDataAvailablePromiseRef.current) {
                onDataAvailablePromiseRef.current.resolve(blob)
                onDataAvailablePromiseRef.current = null
            }
        }

        // Record start time
        recordingStartTimeRef.current = Date.now()

        mediaRecorder.start()
        setIsRecording(true)
        // Use the explicitly passed duration so the timer always starts at the current question's duration
        const duration = questionDuration ?? currentQuestionData?.duration ?? 120
        setTimeRemaining(duration)
    }

    // Timer effect
    useEffect(() => {
        if (interviewState !== 'recording' || !isRecording || isUploading || timeRemaining <= 0) {
            return
        }

        const timer = setInterval(() => {
            if (timeRemaining <= 1) {
                clearInterval(timer)
                setTimeRemaining(0)
                console.log('Timer expired for question', currentQuestionIndex + 1)
                void handleNextQuestion()
                return
            }

            setTimeRemaining(timeRemaining - 1)
        }, 1000)

        return () => clearInterval(timer)
    }, [interviewState, isRecording, currentQuestionIndex, isUploading, timeRemaining])

    // Handle next question
    const handleNextQuestion = async () => {
        console.log('handleNextQuestion called. Current Index:', currentQuestionIndex)
        if (!isRecording || isUploading) return

        const storedCode = localStorage.getItem('interview_access_code')
        if (!storedCode) {
            setError(t('interview.page.sessionError', 'Failed to recover session. Please log in again.'))
            setInterviewState('error')
            return
        }

        setIsUploading(true)
        const wasFinalQuestion = currentQuestionIndex >= totalQuestions - 1

        try {
            // Stop recorder and wait for blob
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                const blobPromise = new Promise<Blob>((resolve, reject) => {
                    onDataAvailablePromiseRef.current = { resolve, reject }
                    mediaRecorderRef.current?.stop()
                })

                const videoBlob = await blobPromise
                console.log('Segment blob captured:', videoBlob.size)

                // Upload segment
                const questionText = currentQuestion
                await interviewAudioApi.uploadVideoResponse(
                    interviewId,
                    currentQuestionIndex,
                    questionText,
                    videoBlob,
                    storedCode,
                    currentQuestionDuration - timeRemaining
                )

                // Show success message
                setShowSuccessToast(true)
                // Wait for the user to see the message
                await new Promise(resolve => setTimeout(resolve, 2000))
                setShowSuccessToast(false)
            }

            if (!wasFinalQuestion) {
                const nextIndex = currentQuestionIndex + 1
                console.log('Proceeding to next question:', nextIndex)

                setCurrentQuestionIndex(nextIndex)

                // Save progress to backend and get next question
                if (interviewId) {
                    try {
                        const response = await interviewAudioApi.updateCurrentQuestion(interviewId, nextIndex, storedCode)
                        if (response.data.current_question) {
                            setCurrentQuestionData(response.data.current_question)
                            const duration = response.data.current_question.duration || 120

                            // Start recording the next segment, passing duration explicitly
                            // so the timer starts at the correct value for each question
                            if (streamRef.current) {
                                startRecordingSegment(streamRef.current, duration)
                            }
                        }
                    } catch (err) {
                        console.error('Failed to update progress:', err)
                        // Even if update progress fails, we might want to continue or show error
                        setError(t('interview.page.saveError', 'Failed to load next question.'))
                    }
                }
            } else {
                console.log('Final question reached. Interview completed.')
                if (videoRef.current) {
                    videoRef.current.srcObject = null
                }
                setInterviewState('completed')
                setIsRecording(false)

                // Finalize interview status on backend
                if (interviewId) {
                    const formData = new FormData()
                    formData.append('consent_given', 'true')
                    if (consentTimestamp) {
                        formData.append('consent_timestamp', consentTimestamp)
                    }
                    await interviewAudioApi.completeInterview(interviewId, storedCode, formData)
                }

                // Cleanup tracks
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop())
                }
            }
        } catch (err: any) {
            console.error('Failed to save response segment:', err)
            setError(t('interview.page.saveError', 'Failed to save your response. Please try again.'))
        } finally {
            setIsUploading(false)
        }
    }


    // Cleanup
    useEffect(() => {
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current)
            }
            if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
                mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop())
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop())
            }
        }
    }, [])

    // Effect to attach stream to video element when recording starts
    useEffect(() => {
        if (interviewState === 'recording' && videoRef.current && streamRef.current) {
            videoRef.current.srcObject = streamRef.current
            videoRef.current.play().catch(console.error)
        }
    }, [interviewState])

    // Format time
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }


    return (
        <div className="min-h-screen bg-background flex flex-col" translate="no">
            <header className="border-b h-16 flex items-center px-8 justify-between">
                <div className="flex items-center space-x-4">
                    <img src={logoSrc} alt="Selektia" className="h-8 w-auto" />
                    <span className="font-semibold text-lg text-muted-foreground">| {offerTitle}</span>
                </div>
                <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-3">
                        <div className="text-right text-xs">
                            <div className="font-medium text-foreground">{candidateName}</div>
                            <div className="text-muted-foreground">{t('interview.page.candidateDefault')}</div>
                        </div>
                        {candidatePhoto ? (
                            <img
                                src={candidatePhoto}
                                alt={candidateName}
                                className="h-10 w-10 rounded-full object-cover border-2 border-primary/20"
                            />
                        ) : (
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                                {candidateName.charAt(0).toUpperCase()}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="flex-1 container mx-auto py-12 px-4 max-w-full">
                {isLoading ? (
                    <div className="max-w-4xl mx-auto px-4">
                        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
                            <Loader2 className="h-12 w-12 animate-spin text-primary" />
                            <p className="text-muted-foreground animate-pulse">{t('common.loading', 'Loading interview...')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Wrap initial states in a focused container */}
                        {(interviewState === 'welcome' ||
                            interviewState === 'already_completed' ||
                            interviewState === 'deadline_exceeded') && (
                                <div className="max-w-4xl mx-auto px-4">

                                    {interviewState === 'welcome' && (
                                        <>
                                            <div className="text-center mb-12">
                                                <h1 className="text-3xl font-bold mb-4">
                                                    {savedQuestionIndex > 0 ? t('interview.page.resumeTitle', 'Resume Your Interview') : t('interview.page.readyTitle')}
                                                </h1>
                                                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                                                    {savedQuestionIndex > 0 ? (
                                                        <Trans i18nKey="interview.page.questionsRemaining" values={{ current: savedQuestionIndex + 1, total: totalQuestions }}>
                                                            You are resuming from question <span className="font-bold">{savedQuestionIndex + 1}</span> of <span className="font-bold">{totalQuestions}</span>.
                                                        </Trans>
                                                    ) : (
                                                        <Trans i18nKey="interview.page.questionsCount" values={{ count: totalQuestions }}>
                                                            You will be presented with <span className="font-bold">{totalQuestions} questions</span>.
                                                        </Trans>
                                                    )}
                                                </p>
                                            </div>

                                            <div className="grid gap-6 md:grid-cols-2 mb-8">
                                                <div className="bg-card border rounded-lg p-6 shadow-sm">
                                                    <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                                        <Video className="h-5 w-5 text-primary" />
                                                    </div>
                                                    <h3 className="font-semibold text-lg mb-2">{t('interview.page.videoRecordingTitle')}</h3>
                                                    <p className="text-muted-foreground">
                                                        {t('interview.page.videoRecordingDesc')}
                                                    </p>
                                                </div>

                                                <div className="bg-card border rounded-lg p-6 shadow-sm">
                                                    <div className="h-10 w-10 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                                                        <Clock className="h-5 w-5 text-green-600 dark:text-green-400" />
                                                    </div>
                                                    <h3 className="font-semibold text-lg mb-2">{t('interview.page.timedQuestionsTitle')}</h3>
                                                    <p className="text-muted-foreground">
                                                        {t('interview.page.timedQuestionsDesc')}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Critical Warnings */}
                                            <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-6 mb-12 flex items-start gap-4">
                                                <div className="h-10 w-10 bg-destructive/10 rounded-full flex items-center justify-center shrink-0">
                                                    <AlertTriangle className="h-5 w-5 text-destructive" />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-destructive mb-1">{t('interview.page.importantNotice')}</h3>
                                                    <ul className="text-sm space-y-2 text-muted-foreground">
                                                        <li className="flex items-start gap-2">
                                                            <span className="h-1.5 w-1.5 rounded-full bg-destructive shrink-0 mt-1.5" />
                                                            <span>
                                                                <span className="font-semibold text-foreground">{t('interview.page.oneShot')}</span>{' '}
                                                                <Trans i18nKey="interview.page.oneShotDesc">
                                                                    You can complete the interview in several sessions, as long as you finish <span className="underline decoration-destructive/30 decoration-2 underline-offset-2">before the deadline</span>.
                                                                </Trans>
                                                            </span>
                                                        </li>
                                                        <li className="flex items-start gap-2">
                                                            <span className="h-1.5 w-1.5 rounded-full bg-destructive shrink-0 mt-1.5" />
                                                            <span>
                                                                <span className="font-semibold text-foreground">{t('interview.page.noPause')}</span>{' '}
                                                                <Trans i18nKey="interview.page.noPauseDesc">
                                                                    You can <span className="underline decoration-destructive/30 decoration-2 underline-offset-2">stop at a question</span> and come back later from where you left off.
                                                                </Trans>
                                                            </span>
                                                        </li>
                                                        <li className="text-destructive font-medium">
                                                            {t('interview.page.readyWarning')}
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>

                                            <div className="text-center">
                                                <Button size="lg" className="h-14 px-8 text-lg" onClick={startInterview}>
                                                    <Video className="mr-2 h-5 w-5" />
                                                    {savedQuestionIndex > 0 ? t('interview.page.resumeButton', 'Resume Interview') : t('interview.page.startButton')}
                                                </Button>
                                            </div>
                                        </>
                                    )}

                                    {interviewState === 'already_completed' && (
                                        <div className="text-center py-12">
                                            <div className="h-20 w-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                                <CheckCircle className="h-10 w-10 text-primary" />
                                            </div>
                                            <h2 className="text-3xl font-bold mb-4">{t('interview.page.alreadyCompletedTitle')}</h2>
                                            <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-8">
                                                {t('interview.page.alreadyCompletedDesc')}
                                            </p>
                                            <Button variant="outline" onClick={() => navigate('/interview_audio/login')}>
                                                {t('interview.page.returnLogin')}
                                            </Button>
                                        </div>
                                    )}

                                    {interviewState === 'deadline_exceeded' && (
                                        <div className="text-center py-12">
                                            <div className="h-20 w-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                                <AlertTriangle className="h-10 w-10 text-destructive" />
                                            </div>
                                            <h2 className="text-3xl font-bold mb-4">{t('interview.page.deadlineExceededTitle')}</h2>
                                            <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-8">
                                                {t('interview.page.deadlineExceededDesc')}
                                            </p>
                                            <Button variant="outline" onClick={() => navigate('/interview_audio/login')}>
                                                {t('interview.page.returnLogin')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                        {/* Recording Screen */}
                        {interviewState === 'recording' && (
                            <div className="max-w-[1800px] mx-auto px-8">
                                {/* Progress at the top */}
                                <div className="mb-12">
                                    <div className="flex justify-between text-sm text-muted-foreground mb-4">
                                        <div className="flex items-center gap-2">
                                            <span className="bg-primary/10 text-primary px-3 py-1 rounded-full font-bold">
                                                {t('interview.page.questionProgress', { current: currentQuestionIndex + 1, total: totalQuestions })}
                                            </span>
                                        </div>
                                        <span className="font-medium text-muted-foreground">
                                            {Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100)}{t('interview.page.percentComplete')}
                                        </span>
                                    </div>
                                    <div className="h-2.5 bg-muted rounded-full overflow-hidden shadow-sm">
                                        <div
                                            className="h-full bg-primary transition-all duration-700 ease-out"
                                            style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Main Content Grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                                    {/* Left Column: Questions / Confirmation (7/12 width) */}
                                    <div className="lg:col-span-7 space-y-6">
                                        <div className="bg-card border border-primary/10 rounded-2xl p-10 shadow-xl relative overflow-hidden transition-all duration-500">
                                            <div className="absolute top-0 left-0 w-1.5 h-full bg-primary" />
                                            {showSuccessToast ? (
                                                <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                    <div className="h-12 w-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-6">
                                                        <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                                                    </div>
                                                    <h2 className="text-2xl font-bold text-green-800 dark:text-green-300 mb-6 leading-tight">
                                                        {t('interview.page.savedConfirmation', 'Votre réponse a bien été enregistrée')}
                                                    </h2>
                                                    <div className="space-y-2">
                                                        <div className="h-2 w-full bg-green-100 dark:bg-green-900/20 rounded-full overflow-hidden">
                                                            <div className="h-full bg-green-600 animate-progress-fast" />
                                                        </div>
                                                        <p className="text-xs text-green-600 dark:text-green-400 font-medium animate-pulse">
                                                            {t('interview.page.loadingNext', 'Préparation de la question suivante...')}
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {isTranslating && !translatedQuestions[currentQuestionIndex] ? (
                                                        <div className="flex items-center gap-3 text-3xl font-bold mb-6 leading-tight text-muted-foreground italic">
                                                            <Loader2 className="h-6 w-6 animate-spin" />
                                                            <span>{t('interview.page.translating', 'Translating...')}</span>
                                                        </div>
                                                    ) : (
                                                        <h2 className="text-3xl font-bold mb-6 text-foreground leading-tight">
                                                            {currentQuestion}
                                                        </h2>
                                                    )}
                                                    <div className="flex items-center gap-2 text-muted-foreground bg-muted/30 px-4 py-2 rounded-lg inline-flex">
                                                        <Clock className="h-5 w-5 text-primary" />
                                                        <span className="text-base font-medium">{t('interview.page.timeLimit', { time: formatTime(currentQuestionDuration) })}</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right Column: Video Preview & Controls (5/12 width) */}
                                    <div className="lg:col-span-5 flex flex-col gap-8">
                                        <div className="rounded-2xl overflow-hidden shadow-2xl bg-black aspect-video relative border-4 border-white dark:border-muted-foreground/10 ring-1 ring-black/5">
                                            <InterviewCameraPreview videoRef={videoRef} />

                                            {isRecording && (
                                                <div className="absolute top-6 right-6 flex items-center space-x-3 bg-red-500/90 text-white px-4 py-2 rounded-full text-base font-bold backdrop-blur-md shadow-lg">
                                                    <div className="h-2.5 w-2.5 bg-white rounded-full animate-pulse" />
                                                    <span className="tracking-wide uppercase text-xs">{t('interview.page.recordingStatus')}</span>
                                                </div>
                                            )}

                                            {/* Timer overlay on video */}
                                            <div className="absolute top-6 left-6 bg-black/70 text-white px-5 py-2.5 rounded-xl text-2xl font-mono font-black backdrop-blur-md border border-white/20 shadow-2xl transition-all duration-300">
                                                {formatTime(timeRemaining)}
                                            </div>
                                        </div>

                                        {/* Next button centered below video */}
                                        <div className="flex justify-center">
                                            <Button
                                                size="lg"
                                                onClick={() => handleNextQuestion()}
                                                disabled={!isRecording || isUploading || showSuccessToast}
                                                className="min-w-[280px] h-14 text-lg font-bold shadow-xl hover:shadow-2xl transition-all relative overflow-hidden group"
                                            >
                                                {isUploading ? (
                                                    <>
                                                        <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                                                        {t('interview.page.saving', 'Enregistrement...')}
                                                    </>
                                                ) : showSuccessToast ? (
                                                    <>
                                                        <CheckCircle className="mr-3 h-5 w-5 text-green-500" />
                                                        {t('interview.page.savedConfirmation', 'Réponse enregistrée !')}
                                                    </>
                                                ) : (
                                                    <>
                                                        {currentQuestionIndex < totalQuestions - 1
                                                            ? t('interview.page.nextButton')
                                                            : t('interview.page.finishButton')}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        )}

                        {/* Wrap terminal states in focused container */}
                        {(interviewState === 'uploading' ||
                            interviewState === 'completed' ||
                            interviewState === 'error') && (
                                <div className="max-w-4xl mx-auto px-4">
                                    {interviewState === 'uploading' && (
                                        <div className="text-center max-w-md mx-auto">
                                            <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                            </div>
                                            <h2 className="text-2xl font-bold mb-4">{t('interview.page.uploadingTitle')}</h2>
                                            <p className="text-muted-foreground mb-6">
                                                {t('interview.page.uploadingDesc')}
                                            </p>
                                            <div className="space-y-2">
                                                <Progress value={uploadProgress} className="h-2" />
                                                <p className="text-sm font-medium text-primary">{uploadProgress}%</p>
                                            </div>
                                        </div>
                                    )}

                                    {interviewState === 'completed' && (
                                        <div className="text-center max-w-md mx-auto">
                                            <div className="h-16 w-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                                                <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
                                            </div>
                                            <h2 className="text-2xl font-bold mb-4">{t('interview.page.completedTitle')}</h2>
                                            <p className="text-muted-foreground mb-8">
                                                {t('interview.page.completedDesc')}
                                            </p>
                                            <Button onClick={() => navigate('/interview_audio/login')}>
                                                {t('interview.page.returnLogin')}
                                            </Button>
                                        </div>
                                    )}

                                    {interviewState === 'error' && (
                                        <div className="text-center max-w-md mx-auto">
                                            <div className="h-16 w-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                                <AlertCircle className="h-10 w-10 text-destructive" />
                                            </div>
                                            <h2 className="text-2xl font-bold mb-4">{t('interview.page.errorTitle')}</h2>
                                            <p className="text-muted-foreground mb-8">{error}</p>
                                            <Button onClick={() => navigate('/interview_audio/login')}>
                                                {t('interview.page.returnLogin')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                        {/* Debug: Display Questions */}
                        {/* <div className="mt-8 p-4 bg-muted/50 rounded-lg border border-dashed">
                    <h4 className="font-mono text-xs uppercase mb-3 text-muted-foreground">Debug: Questions Data</h4>

                    <div className="space-y-2 text-sm">
                        <div>
                            <span className="font-semibold">Total Questions:</span> {allQuestions.length}
                        </div>
                        <div>
                            <span className="font-semibold">Current Index:</span> {currentQuestionIndex}
                        </div>
                        <div>
                            <span className="font-semibold">Interview ID:</span> {interviewId || 'Not set'}
                        </div>
                    </div>
                    <div className="mt-4">
                        <div className="font-semibold text-sm mb-2">Questions List:</div>
                        <div className="bg-background rounded p-3 max-h-60 overflow-auto">
                            {allQuestions.length > 0 ? (
                                <ol className="list-decimal list-inside space-y-1 text-sm">
                                    {allQuestions.map((q, i) => (
                                        <li key={i} className={i === currentQuestionIndex ? 'font-bold text-primary' : ''}>
                                            {q.text} <span className="text-muted-foreground text-xs">({q.duration}s)</span>
                                        </li>
                                    ))}
                                </ol>
                            ) : (
                                <p className="text-muted-foreground text-sm">No questions loaded</p>
                            )}
                        </div>
                    </div>

                    <div className="mt-4">
                        <div className="font-semibold text-sm mb-2">Raw Questions Object:</div>
                        <pre className="text-xs overflow-auto max-h-40 bg-background rounded p-3">
                            {JSON.stringify(questions, null, 2)}
                        </pre>
                    </div>
                </div> */}
                        <Modal
                            isOpen={showConsentModal}
                            onClose={() => { }}
                            title={t('interview.page.consentTitle')}
                            hideCloseButton={true}
                            size="md"
                            footer={
                                <div className="flex justify-end">
                                    <Button
                                        onClick={async () => {
                                            if (language === 'custom' && selectedLanguage) {
                                                await handleLanguageSelection(selectedLanguage)
                                            }
                                            setShowConsentModal(false)
                                            if (!consentTimestamp) {
                                                setConsentTimestamp(new Date().toISOString())
                                            }
                                        }}
                                        disabled={!hasAcceptedConsent || (language === 'custom' && !selectedLanguage)}
                                    >
                                        {t('interview.page.acceptButton')}
                                    </Button>
                                </div>
                            }
                        >
                            <div className="space-y-4">
                                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground bg-muted/30 p-4 rounded-lg">
                                    <li>{t('interview.page.consentRule1')}</li>
                                    <li>{t('interview.page.consentRule2')}</li>
                                    <li>{t('interview.page.consentRule3')}</li>
                                    <li>{t('interview.page.consentRule4')}</li>
                                </ul>

                                {language === 'custom' && (
                                    <div className="space-y-3 py-4 border-y">
                                        <div className="flex flex-col gap-1">
                                            <label className="text-sm font-semibold flex items-center gap-2">
                                                <Globe className="w-4 h-4 text-primary" />
                                                {t('interview.page.selectLanguage', 'Langue de l’entretien')}
                                            </label>
                                            <p className="text-[12px] text-red-500">
                                                {t('interview.page.selectLanguageWarning')}
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <Button
                                                variant={selectedLanguage === 'fr' ? 'default' : 'outline'}
                                                className={`h-12 flex items-center justify-center gap-2 transition-all ${selectedLanguage === 'fr' ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                                                onClick={() => setSelectedLanguage('fr')}
                                            >
                                                <span className="text-xl">🇫🇷</span>
                                                <span>French</span>
                                            </Button>
                                            <Button
                                                variant={selectedLanguage === 'en' ? 'default' : 'outline'}
                                                className={`h-12 flex items-center justify-center gap-2 transition-all ${selectedLanguage === 'en' ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                                                onClick={() => setSelectedLanguage('en')}
                                            >
                                                <span className="text-xl">🇺🇸</span>
                                                <span>English</span>
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                <div className="pt-2">
                                    <Checkbox
                                        id="consent-checkbox"
                                        checked={hasAcceptedConsent}
                                        onChange={(e) => setHasAcceptedConsent(e.target.checked)}
                                        label={t('interview.page.consentCheckbox')}
                                    />
                                </div>
                            </div>
                        </Modal>

                        {/* Interview demo guide (screenshots) */}
                        <InterviewOnboardingModal
                            isOpen={showOnboardingModal}
                            onComplete={completeOnboarding}
                        />

                        {/* Device Test Modal */}
                        <DeviceTestModal
                            isOpen={showDeviceTestModal}
                            onClose={() => setShowDeviceTestModal(false)}
                            onTestComplete={() => {
                                setShowDeviceTestModal(false)
                                startRecordingAfterDeviceTest()
                            }}
                        />
                    </>
                )}
            </main >
        </div >
    )
}
